import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { X, Search, BookOpen, Clock, Plus, ChevronDown, ChevronRight } from '../../icons';
import { createTeam, addTeamMember, type TeamDto } from '../../api/project-teams';
import { searchUsers, type UserSummary } from '../../api/users';
import { useAuthStore } from '../../store/authStore';
import {
  listDivisions, listAllTeams, listDirectMembers, getTeam,
  type DivisionDto, type UserSummary as OrgUserSummary,
} from '../../api/organization';
import { Avatar } from '../../components/shared';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './TeamCreateInviteModal.css';

interface TeamCreateInviteModalProps {
  onClose: () => void;
  onCreated: (team: TeamDto) => void;
  isLeaving?: boolean;
}

/** 선택된 사람 — 검색 결과와 조직도 결과의 공통 최소 형태. */
interface Picked {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

/** 한 화면에 펼치는 팀원 수 — 나머지는 "+ N명 더 보기". */
const MEMBER_PREVIEW = 6;

/**
 * TeamCreateInviteModal (AW-003, 2026-08-18) — **새 팀 만들기(멤버 초대)**.
 *
 * 설계서의 지적은 "팀 생성이 너무 복잡하다" 였다. 3단계 위저드(TeamSetupWizardModal)를
 * 한 장으로 접고, 초대는 **선택**임을 화면에서 못 박는다:
 *   - 이름만 채우면 [팀 만들기] 가 활성 — 초대 없이 그대로 만들 수 있다.
 *   - [나중에 초대하기 →] 를 하단에 따로 노출해 건너뛰는 길을 눈에 보이게 둔다.
 *   - 찾기 방식 이원화: 이름/이메일 검색(최근 협업자) + **조직도 계층 트리**(부서별 찾기).
 *     이름이 가물가물해도 상위 조직 → 부서 → 구성원 순으로 훑어보고 고를 수 있다.
 *
 * 기존 위저드·모달 파일은 보존한다(소스 회귀 금지) — 이 파일은 신규이고, 호출부만 바꿔 끼운다.
 * 팀 생성 API 는 기존 그대로 쓴다: createTeam → 선택 인원만큼 addTeamMember.
 */
export function TeamCreateInviteModal({ onClose, onCreated, isLeaving = false }: TeamCreateInviteModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  const [name, setName] = useState('');
  // 화면구성도 V0.1 슬라이드 4 — "스페이스 생성에 설명만 추가". 선택 입력이라 비워 두면 종전과 같다.
  const [description, setDescription] = useState('');
  const [tab, setTab] = useState<'recent' | 'dept'>('recent');
  const [picked, setPicked] = useState<Picked[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 이름이 비어 제출이 막힐 때, 어디를 채워야 하는지 그 칸으로 데려가기 위한 참조.
  const nameRef = useRef<HTMLInputElement | null>(null);
  /**
   * 팀은 만들어졌는데 초대 일부가 실패한 상태.
   * 이때 모달을 그냥 닫으면 실패 사실이 눈에 띄지 않고 사라진다 — 열어 둔 채 알리고,
   * 닫을 때 목록 갱신은 하도록 만든 팀을 여기 들고 있는다.
   */
  const [created, setCreated] = useState<TeamDto | null>(null);
  // 나는 팀을 만드는 사람이라 자동으로 팀장이 된다 — 초대 후보에서 뺀다(중복 추가 409 방지).
  const authUser = useAuthStore((st) => st.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';

  // 최근 협업자 탭 — 300ms debounce 검색(기존 초대 모달과 같은 방식).
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 부서별 찾기 탭 — 조직도(부서 → 팀 → 구성원). 팀 멤버는 펼칠 때 1회만 조회한다.
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [orgTeams, setOrgTeams] = useState<Array<{ id: string; divisionId: string | null; name: string }>>([]);
  const [directMembers, setDirectMembers] = useState<OrgUserSummary[]>([]);
  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({});
  const [teamMembers, setTeamMembers] = useState<Record<string, OrgUserSummary[]>>({});
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({});
  const [orgLoaded, setOrgLoaded] = useState(false);

  useEscapeClose(finish, submitting);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        setSearching(true);
        try {
          const found = await searchUsers(q.trim(), 20);
          setUsers(myId ? found.filter((u) => u.id !== myId) : found);
        } catch {
          setUsers([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, myId]);

  // 조직도는 탭을 처음 열 때만 불러온다 — 팀 생성만 하려는 사람에게 불필요한 요청을 만들지 않는다.
  useEffect(() => {
    if (tab !== 'dept' || orgLoaded) return;
    setOrgLoaded(true);
    void (async () => {
      const [divs, teams, direct] = await Promise.all([
        listDivisions().catch(() => []),
        listAllTeams().catch(() => []),
        listDirectMembers().catch(() => []),
      ]);
      setDivisions(divs);
      setOrgTeams(teams.map((t) => ({ id: t.id, divisionId: t.divisionId, name: t.name })));
      setDirectMembers(myId ? direct.filter((u) => u.id !== myId) : direct);
    })();
  }, [tab, orgLoaded]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);

  function toggle(p: Picked) {
    setPicked((cur) => (cur.some((x) => x.id === p.id) ? cur.filter((x) => x.id !== p.id) : [...cur, p]));
  }

  async function openTeam(teamId: string) {
    setExpandedTeams((e) => ({ ...e, [teamId]: !e[teamId] }));
    if (teamMembers[teamId]) return;
    try {
      const detail = await getTeam(teamId);
      const list = detail.members.map((x) => x.user);
      setTeamMembers((m) => ({ ...m, [teamId]: myId ? list.filter((u) => u.id !== myId) : list }));
    } catch {
      setTeamMembers((m) => ({ ...m, [teamId]: [] }));
    }
  }

  /** 닫기 — 팀이 이미 만들어졌으면 목록을 갱신하도록 알리고 닫는다. */
  function finish() {
    if (created) onCreated(created);
    else onClose();
  }

  async function submit(withMembers: boolean) {
    // 이름이 비면 **버튼을 죽이지 않고** 눌렀을 때 이유를 말해 준다.
    // 비활성 버튼은 "왜 안 되는지" 를 화면에서 지워 버려서, 멤버만 고른 사용자가
    // 만들기를 눌러도 아무 일이 없는 것처럼 보였다(2026-08-19 신고).
    if (!name.trim()) {
      setError(tProject('project.team.create.err_name'));
      nameRef.current?.focus();
      nameRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const team = await createTeam({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      if (withMembers && picked.length > 0) {
        const results = await Promise.allSettled(
          picked.map((p) => addTeamMember(team.id, { userId: p.id })),
        );
        // 409 = 이미 이 팀의 멤버 — 원하던 결과가 이미 이뤄진 것이라 실패로 세지 않는다.
        const failed = picked.filter((_, i) => {
          const r = results[i];
          if (r?.status !== 'rejected') return false;
          return (r.reason as { response?: { status?: number } })?.response?.status !== 409;
        });
        // 팀은 이미 만들어졌다 — 되돌리지 않는다. 대신 **모달을 열어 둔 채** 누가 실패했는지 알린다.
        if (failed.length > 0) {
          setCreated(team);
          setError(tProject('project.team.aw.err_invite_n', {
            names: failed.map((f) => f.name).join(', '),
          }));
          setSubmitting(false);
          return;
        }
      }
      onCreated(team);
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.team.create.err_save')}`);
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`pj-modal-backdrop${isLeaving ? ' pj-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting && !isLeaving) finish(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.team.create.title')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`pj-modal tci-modal${isLeaving ? ' pj-modal--leaving' : ''}`}>
        <header className="tci-head">
          <div>
            <h2 className="tci-title">{tProject('project.team.create.title')}</h2>
            <p className="tci-sub">{tProject('project.team.aw.subtitle')}</p>
          </div>
          <button type="button" className="pj-modal-close" onClick={finish} disabled={submitting} aria-label={tProject('project.btn.close')} data-tip={tProject('project.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="tci-body">
          <label className="tci-label" htmlFor="tci-name">{tProject('project.team.create.label_name')}</label>
          <input
            id="tci-name"
            ref={nameRef}
            className="tci-input"
            aria-invalid={error != null && !name.trim()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tProject('project.team.create.placeholder_name')}
            maxLength={100}
            autoFocus
            disabled={submitting}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) void submit(true); }}
          />

          <label className="tci-label tci-label--mt" htmlFor="tci-desc">{tProject('project.team.aw.desc_label')}</label>
          <textarea
            id="tci-desc"
            className="tci-input tci-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={tProject('project.team.aw.desc_ph')}
            rows={2}
            maxLength={5000}
            disabled={submitting}
          />

          <div className="tci-label tci-label--mt">{tProject('project.team.aw.invite_label')}</div>

          <div className="tci-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'recent'}
              className={`tci-tab${tab === 'recent' ? ' tci-tab--on' : ''}`}
              onClick={() => setTab('recent')}
            >
              <Clock size={13} aria-hidden />
              {tProject('project.team.aw.tab_recent')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'dept'}
              className={`tci-tab${tab === 'dept' ? ' tci-tab--on' : ''}`}
              onClick={() => setTab('dept')}
            >
              <BookOpen size={13} aria-hidden />
              {tProject('project.team.aw.tab_dept')}
            </button>
          </div>

          {picked.length > 0 ? (
            <div className="tci-picked">
              <span className="tci-picked-count">{tProject('project.team.aw.selected', { n: String(picked.length) })}</span>
              {picked.map((p) => (
                <span key={p.id} className="tci-picked-chip">
                  <Avatar userId={Number(p.id) || 0} name={p.name} src={p.avatarUrl ?? null} size="xxs" />
                  {p.name}
                  <button
                    type="button"
                    aria-label={tProject('project.team.aw.remove_aria', { name: p.name })}
                    onClick={() => toggle(p)}
                  >
                    <X size={11} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            tab === 'recent' && <p className="tci-hint">{tProject('project.team.aw.none_selected')}</p>
          )}

          {tab === 'recent' && (
            <>
              <div className="tci-search">
                <Search size={13} aria-hidden />
                <input
                  className="tci-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={tProject('project.team.aw.search_ph')}
                  aria-label={tProject('project.team.aw.search_ph')}
                  disabled={submitting}
                />
              </div>
              <ul className="tci-list scrollbar-thin">
                {!searching && users.length === 0 && (
                  <li className="tci-empty">{tProject('project.team.aw.empty_search')}</li>
                )}
                {users.map((u) => (
                  <li key={u.id} className="tci-person">
                    <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl} size="sm" />
                    <span className="tci-person-body">
                      <b>{u.name}</b>
                      <small>{u.email}</small>
                    </span>
                    <button
                      type="button"
                      className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
                      aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
                      onClick={() => toggle({ id: u.id, name: u.name, avatarUrl: u.avatarUrl })}
                    >
                      {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {tab === 'dept' && (
            <div className="tci-org scrollbar-thin">
              {divisions.length === 0 && orgTeams.length === 0 && directMembers.length === 0 && (
                <p className="tci-empty">{tProject('project.team.aw.empty_org')}</p>
              )}

              {divisions.map((d) => {
                const teams = orgTeams.filter((t) => t.divisionId === d.id);
                const open = openNodes[d.id] ?? false;
                const headcount = teams.reduce((n, t) => n + (teamMembers[t.id]?.length ?? 0), 0);
                return (
                  <div className="tci-node" key={d.id}>
                    <button type="button" className="tci-node-head" onClick={() => setOpenNodes((o) => ({ ...o, [d.id]: !open }))}>
                      {open ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
                      <span className="tci-node-name">{d.name}</span>
                      {headcount > 0 && (
                        <span className="tci-node-count">{tProject('project.team.aw.count_people', { n: String(headcount) })}</span>
                      )}
                    </button>
                    {open && teams.map((t) => (
                      <TeamNode
                        key={t.id}
                        name={t.name}
                        expanded={expandedTeams[t.id] ?? false}
                        members={teamMembers[t.id]}
                        pickedIds={pickedIds}
                        onToggleOpen={() => void openTeam(t.id)}
                        onPick={toggle}
                      />
                    ))}
                    {open && teams.length === 0 && <p className="tci-empty tci-empty--sub">{tProject('project.team.aw.empty_org')}</p>}
                  </div>
                );
              })}

              {/* 부서 없는 팀 — 조직도에서 사라지지 않게 같은 층에 편다. */}
              {orgTeams.filter((t) => !t.divisionId).map((t) => (
                <TeamNode
                  key={t.id}
                  name={t.name}
                  expanded={expandedTeams[t.id] ?? false}
                  members={teamMembers[t.id]}
                  pickedIds={pickedIds}
                  onToggleOpen={() => void openTeam(t.id)}
                  onPick={toggle}
                />
              ))}

              {/* 팀에 속하지 않은 회사 직속 구성원(대표 등). */}
              {directMembers.map((u) => (
                <div className="tci-person tci-person--flat" key={u.id}>
                  <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl ?? null} size="sm" />
                  <span className="tci-person-body">
                    <b>{u.name}</b>
                    <small>{u.position ?? u.email}</small>
                  </span>
                  <button
                    type="button"
                    className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
                    aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
                    onClick={() => toggle({ id: u.id, name: u.name, avatarUrl: u.avatarUrl ?? null })}
                  >
                    {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
                  </button>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* 에러는 **푸터 바로 위**에 둔다 — 본문이 스크롤되는 모달이라
            본문 안에 두면 버튼을 누른 사람 눈에 안 들어온다. */}
        {error && <div className="tci-error" role="alert">{error}</div>}

        <footer className="tci-foot">
          <button type="button" className="tci-cancel" onClick={finish} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
          {!created && (
            <button type="button" className="tci-later" onClick={() => void submit(false)} disabled={submitting}>
              {tProject('project.team.aw.later')}
            </button>
          )}
          {/* 팀이 이미 만들어진 뒤에는 [만들기] 를 다시 누르면 팀이 하나 더 생긴다 — 닫기로 바꾼다. */}
          <button
            type="button"
            className="tci-submit"
            onClick={() => (created ? finish() : void submit(true))}
            disabled={submitting}
          >
            {created
              ? tProject('project.btn.close')
              : picked.length > 0
                ? tProject('project.team.aw.submit_n', { n: String(picked.length) })
                : tProject('project.team.aw.submit')}
          </button>
        </footer>
      </div>
    </div>
  );
}

interface TeamNodeProps {
  name: string;
  expanded: boolean;
  members: OrgUserSummary[] | undefined;
  pickedIds: Set<string>;
  onToggleOpen: () => void;
  onPick: (p: Picked) => void;
}

/** 조직도 팀 노드 — 펼치면 구성원 체크리스트. 인원이 많으면 앞 6명만 먼저 보여 준다. */
function TeamNode({ name, expanded, members, pickedIds, onToggleOpen, onPick }: TeamNodeProps) {
  const [showAll, setShowAll] = useState(false);
  const list = members ?? [];
  const shown = showAll ? list : list.slice(0, MEMBER_PREVIEW);
  const rest = list.length - shown.length;
  return (
    <div className="tci-node tci-node--team">
      <button type="button" className="tci-node-head" onClick={onToggleOpen}>
        {expanded ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span className="tci-node-name">{name}</span>
        {members && (
          <span className="tci-node-count">{tProject('project.team.aw.count_people', { n: String(list.length) })}</span>
        )}
      </button>
      {expanded && shown.map((u) => (
        <div className="tci-person tci-person--indent" key={u.id}>
          <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl ?? null} size="sm" />
          <span className="tci-person-body">
            <b>{u.name}</b>
            <small>{u.position ?? u.email}</small>
          </span>
          <button
            type="button"
            className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
            aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
            onClick={() => onPick({ id: u.id, name: u.name, avatarUrl: u.avatarUrl ?? null })}
          >
            {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
          </button>
        </div>
      ))}
      {expanded && rest > 0 && (
        <button type="button" className="tci-more" onClick={() => setShowAll(true)}>
          {tProject('project.team.aw.more_people', { n: String(rest) })}
        </button>
      )}
    </div>
  );
}
